import type { TicketQuery, TicketSort, TicketSortDir } from '../../api/types';
import type { Filters } from '../../components/backlog/FilterBar';

/**
 * 🔴 SORTING IS THE SERVER'S JOB. That has not changed — what changed is how much the server can do.
 *
 * `GET /tickets` used to accept `sort=updated|rank` and nothing else, with no direction, so this file
 * REFUSED to make Assignee, Reporter, Priority, Status, Resolution or Created look sortable: the only
 * way to honour such a click was to re-sort the 50 rows the current page happens to hold, and a table
 * sorted that way disagrees with its own pagination — page 2 full of rows that belong before page 1's,
 * with nothing on screen saying so.
 *
 * ✅ The API now orders by created, priority, status, key and points as well, in either direction, and
 * every one of those is a real ORDER BY over the whole result set. So the headers below became
 * sortable by the server growing the capability — ⛔ NOT by this file relaxing the rule. Nothing here
 * sorts a page in the browser, and the two columns the API still cannot order by still say so.
 *
 * ⚠️ THE RULE THAT OUTLIVES THIS CHANGE: a column is sortable here only if `sort` names an ordering
 * the API implements. A header that sorts client-side is the defect, not the feature.
 */

export interface SortOption {
  value: TicketSort;
  label: string;
  /** What the server actually orders by, for the reader who wants to know. */
  detail: string;
  /** The column this ordering corresponds to, when one exists. `null` for `rank`. */
  column: ColumnId | null;
}

export const SORT_OPTIONS: readonly SortOption[] = [
  {
    value: 'updated',
    label: 'Updated',
    detail: 'updated_at — the API default; newest first unless you ask otherwise',
    column: 'updated',
  },
  {
    value: 'created',
    label: 'Created',
    detail: 'created_at — when the ticket was raised, which is not when it last moved',
    column: 'created',
  },
  {
    value: 'priority',
    label: 'Priority',
    // ⭐ Worth stating on screen: this is the vocabulary's own order, so Critical leads High leads
    //   Medium leads Low. Sorted as text it would read critical, high, low, medium.
    detail: 'priority_def.sort_order — Critical first, by meaning rather than alphabetically',
    column: 'priority',
  },
  {
    value: 'status',
    label: 'Status',
    detail: 'status_def.sort_order — the board’s own left-to-right order',
    column: 'status',
  },
  {
    value: 'key',
    label: 'Key',
    detail: 'project key then ticket number, numerically — so TBS-9 comes before TBS-10',
    column: 'work',
  },
  {
    value: 'points',
    label: 'Story points',
    detail: 'story_points, unestimated tickets last — largest first unless you ask otherwise',
    column: null,
  },
  {
    value: 'rank',
    label: 'Backlog order',
    detail: 'backlog_rank ASC, unranked tickets last — the order the backlog is planned in',
    column: null,
  },
];

export const DEFAULT_SORT: TicketSort = 'updated';

export function parseSort(raw: string | null | undefined): TicketSort {
  return SORT_OPTIONS.some((option) => option.value === raw) ? (raw as TicketSort) : DEFAULT_SORT;
}

/**
 * The direction, or `null` for "the ordering's own".
 *
 * ⛔ THERE IS NO DEFAULT DIRECTION, and inventing one here would be a bug. The API's `updated` is
 *   descending and its `rank` is ascending; sending `dir=desc` with `sort=rank` would silently invert
 *   the backlog. `null` means "send no `dir` at all", which is the only spelling of "whatever this
 *   ordering already does".
 */
export function parseDir(raw: string | null | undefined): TicketSortDir | null {
  return raw === 'asc' || raw === 'desc' ? raw : null;
}

/**
 * The direction a column starts in when you first click it.
 *
 * ⭐ Mirrors the API's own per-ordering default, so the first click never reorders anything the user
 *   did not ask to reorder — clicking `Updated` while already sorted by updated-descending must not
 *   flip to ascending as a side effect of being clicked.
 */
export const NATURAL_DIR: Record<TicketSort, TicketSortDir> = {
  updated: 'desc',
  created: 'desc',
  points: 'desc',
  rank: 'asc',
  priority: 'asc',
  status: 'asc',
  key: 'asc',
};

export type ColumnId =
  | 'work'
  | 'assignee'
  | 'reporter'
  | 'priority'
  | 'status'
  | 'resolution'
  | 'created'
  | 'updated';

export interface ColumnDef {
  id: ColumnId;
  label: string;
  /** The ordering this header selects, or `null` when the API cannot order by it. */
  sort: TicketSort | null;
  /** Shown on hover for a column the API cannot order by. */
  note?: string;
}

/**
 * ⚠️ TWO COLUMNS REMAIN UNSORTABLE, and the note says which and why rather than saying "not
 *   sortable". `assignee` and `reporter` are free text on the ticket with no def table behind them,
 *   and `resolution` — Or's DELIVERY ROUTE, per db/007, not Jira's outcome — has no ordering the API
 *   exposes. ⛔ The old blanket note claimed the API could "only order by Updated or Backlog order",
 *   which is now false; a stale caveat is as misleading as a missing one.
 */
const UNSORTABLE =
  'The API has no ordering for this column, so it is not sortable — sorting one page in the browser would disagree with the next page.';

/** Or's shot 7, left to right. */
export const COLUMNS: readonly ColumnDef[] = [
  // ⭐ `Work` holds the key, and `sort=key` orders by project key then NUMBER, so TBS-9 precedes
  //   TBS-10 rather than following it as a text sort would.
  { id: 'work', label: 'Work', sort: 'key' },
  { id: 'assignee', label: 'Assignee', sort: null, note: UNSORTABLE },
  { id: 'reporter', label: 'Reporter', sort: null, note: UNSORTABLE },
  { id: 'priority', label: 'Priority', sort: 'priority' },
  { id: 'status', label: 'Status', sort: 'status' },
  {
    id: 'resolution',
    label: 'Resolution',
    sort: null,
    // ⚠️ Or's `resolution` is a DELIVERY ROUTE (hotfix / by sprint / database fix), not Jira's
    //    outcome — db/007 says so explicitly and asks that nobody "corrects" it.
    note: UNSORTABLE,
  },
  { id: 'created', label: 'Created', sort: 'created' },
  { id: 'updated', label: 'Updated', sort: 'updated' },
];

export const LIST_PER_PAGE = 50;

/**
 * The request for one page of the List view.
 *
 * ⭐ `sort` and `dir` are IN THE QUERY, which is the whole point of the test that covers this:
 * changing the ordering must change what is ASKED FOR, not how the answer is arranged afterwards.
 */
export function buildListQuery(input: {
  filters: Filters;
  page: number;
  sort: TicketSort;
  dir?: TicketSortDir | null;
  perPage?: number;
}): TicketQuery {
  return {
    projectId: input.filters.projectId,
    status: input.filters.status,
    assignee: input.filters.assignee,
    sprintId: input.filters.sprintId,
    sort: input.sort,
    // ⚠️ `null` sends nothing, which is "this ordering's own direction" — not `desc`.
    dir: input.dir ?? null,
    page: input.page,
    per_page: input.perPage ?? LIST_PER_PAGE,
  };
}
